import { motion } from "framer-motion";
import { ABOUT } from "@/constants/testIds";
import { Code2, Database, Layers, Sparkles } from "lucide-react";

const services = [
  {
    icon: Layers,
    title: "Landing Pages",
    body: "High-conversion, distinctive marketing sites with real motion and copy that lands.",
  },
  {
    icon: Code2,
    title: "Web Apps",
    body: "Booking flows, admin dashboards and internal tools built end-to-end.",
  },
  {
    icon: Database,
    title: "Backends",
    body: "FastAPI + MongoDB APIs, auth, integrations and clean data models.",
  },
  {
    icon: Sparkles,
    title: "AI Features",
    body: "Chat assistants, semantic search and generative UI woven into your product.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid={ABOUT.section}
      className="relative py-24 lg:py-40 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: Portrait + intro */}
          <div className="lg:col-span-5">
            <div className="font-mono-tag text-xs text-[#E2FF31] mb-6">
              // 003 — About
            </div>
            <div
              data-testid={ABOUT.portrait}
              className="relative w-full aspect-[4/5] rounded-xl overflow-hidden border border-white/10 mb-8"
            >
              <img
                src="https://images.unsplash.com/photo-1562813733-b31f71025d54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxkZXZlbG9wZXIlMjBwb3J0cmFpdCUyMHN0dWRpbyUyMGRhcmt8ZW58MHx8fHwxNzgzOTUxMjgzfDA&ixlib=rb-4.1.0&q=85"
                alt="Studio workspace"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                <div className="font-mono-tag text-[10px] text-white/60">
                  // Founder & Sole Builder
                </div>
                <div className="font-display text-xl font-medium mt-1">
                  Elevate — Studio of One
                </div>
              </div>
            </div>
          </div>

          {/* Right: Copy + services */}
          <div className="lg:col-span-7">
            <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-8">
              I build the <span className="text-[#E2FF31]">whole thing.</span>{" "}
              Design, front, back, ship.
            </h2>
            <p className="text-white/65 text-base sm:text-lg leading-relaxed max-w-2xl mb-12">
              No handoffs, no dead weight. You brief me, I return with a
              considered plan and a working build inside a week — designed to
              your brand, coded to hold up. Every project shown above was
              shipped by one person from a blank canvas.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="group p-6 border border-white/10 rounded-xl bg-[#0C0C0C]/60 hover:border-[#E2FF31]/40 hover:bg-[#E2FF31]/[0.03] transition-all"
                >
                  <s.icon
                    size={20}
                    className="text-[#E2FF31] mb-4 group-hover:scale-110 transition-transform"
                  />
                  <div className="font-display text-xl font-medium mb-2">
                    {s.title}
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {s.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
